"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.useFuturesMoneyFields = useFuturesMoneyFields;
var _react = require("react");
var _reduxForm = require("redux-form");
var _reactRedux = require("react-redux");
var _bignumber = require("bignumber.js");
var _useFormValues = require("./useFormValues");
var _useEstimatedTPSLValues = _interopRequireDefault(require("../../FuturesAddEditTPSL/useEstimatedTPSLValues"));
var _instrumentPositionSelectors = require("../../../redux/selectors/instrumentPositionSelectors");
var _selectors = require("../../../redux/futures/selectors");
var _FuturesOrderEntryFormContainer = require("../futures/FuturesOrderEntryForm/FuturesOrderEntryFormContainer");
var _decimalPlacesHelper = require("../../../helpers/decimalPlaces/decimalPlacesHelper");
var _orderHelper = require("../../../helpers/orderHelper");
function _interopRequireDefault(e) { return e && e.__esModule ? e : { "default": e }; }
function useFuturesMoneyFields(maxQuantity) {
  var hasCredit = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
  var dispatch = (0, _reactRedux.useDispatch)();
  var instrument = (0, _reactRedux.useSelector)(_instrumentPositionSelectors.selectedInstrumentSelector);
  var markPrice = (0, _reactRedux.useSelector)(_selectors.futuresInstrumentMarkPriceSelector);
  var lastPrice = (0, _reactRedux.useSelector)(_selectors.futuresLastPriceSelector);
  var formValues = (0, _useFormValues.useFormValues)(_FuturesOrderEntryFormContainer.FUTURES_ORDER_FORM_NAME);
  var isOrderByQty = instrument.Product1Symbol === formValues.orderByProduct;
  var bestAskPrice = (0, _reactRedux.useSelector)(_selectors.futuresInstrumentBestAskPriceSelector);
  var bestBidPrice = (0, _reactRedux.useSelector)(_selectors.futuresInstrumentBestBidPriceSelector);
  var buyEntryPrice = (0, _orderHelper.getFuturesOrderEntryPrice)(formValues, bestAskPrice);
  var sellEntryPrice = (0, _orderHelper.getFuturesOrderEntryPrice)(formValues, bestBidPrice);

  // Determine position type based on Take Profit / Stop Loss vs Last Price
  var determinedSide = (0, _react.useMemo)(function () {
    var takeProfit = parseFloat(formValues.takeProfit);
    var stopLoss = parseFloat(formValues.stopLoss);

    // 3.11.f.1 & 3.11.f.2: If Take Profit exists, use it to determine side
    if (!isNaN(takeProfit) && takeProfit > 0) {
      return takeProfit > lastPrice ? 'Long' : 'Short';
    }

    // 3.11.f.3 & 3.11.f.4: If no Take Profit, use Stop Loss
    if (!isNaN(stopLoss) && stopLoss > 0) {
      return stopLoss < lastPrice ? 'Long' : 'Short';
    }

    // Default to Long if neither is set
    return 'Long';
  }, [formValues.takeProfit, formValues.stopLoss, lastPrice]);
  var entryPrice = determinedSide === 'Long' ? buyEntryPrice : sellEntryPrice;
  var _useEstimatedTPSLValu = (0, _useEstimatedTPSLValues["default"])({
      takeProfit: formValues.takeProfit,
      stopLoss: formValues.stopLoss,
      amount: isOrderByQty ? formValues.quantity : formValues.value,
      side: determinedSide,
      price: entryPrice,
      instrument: instrument
    }),
    estimatedProfitValue = _useEstimatedTPSLValu.estimatedProfitValue,
    estimatedLossValue = _useEstimatedTPSLValu.estimatedLossValue;
  var anchoredField = formValues.anchoredField;
  var isDisabled = !maxQuantity && !hasCredit;
  var isConditionalPricefieldDisabled = formValues.priceOrderType === 'Market';
  var valueDP = instrument ? (0, _decimalPlacesHelper.convertIncrementToIntDecimalPlaces)(instrument.PriceIncrement) : 2;
  var quantityDP = instrument ? (0, _decimalPlacesHelper.convertIncrementToIntDecimalPlaces)(instrument.QuantityIncrement) : 2;
  var minimumQuantity = instrument ? instrument.MinimumQuantity : 0;
  var onChange = (0, _react.useCallback)(function (key, value) {
    return dispatch((0, _reduxForm.change)(_FuturesOrderEntryFormContainer.FUTURES_ORDER_FORM_NAME, key, value));
  }, [dispatch]);
  (0, _react.useEffect)(function () {
    onChange('orderByProduct', instrument === null || instrument === void 0 ? void 0 : instrument.Product1Symbol);
  }, [instrument === null || instrument === void 0 ? void 0 : instrument.Product1Symbol, onChange]);
  (0, _react.useEffect)(function () {
    if (isConditionalPricefieldDisabled) {
      onChange('price', '');
    }
  }, [isConditionalPricefieldDisabled, onChange]);
  (0, _react.useEffect)(function () {
    if (anchoredField === 'quantity') {
      if (typeof formValues.quantity === 'number' && !Number.isNaN(formValues.quantity) && Number.isFinite(formValues.quantity)) {
        if (isOrderByQty) {
          onChange('value', new _bignumber.BigNumber(markPrice * formValues.quantity).toFixed(valueDP));
        } else {
          onChange('value', new _bignumber.BigNumber(formValues.quantity || 0).div(markPrice).toFixed(quantityDP));
        }
      }
    }
  }, [anchoredField, markPrice, isOrderByQty, formValues.quantity, formValues.orderByProduct, quantityDP, valueDP, onChange]);
  var maxAmount = (0, _react.useMemo)(function () {
    return isOrderByQty ? maxQuantity : markPrice * maxQuantity;
  }, [maxQuantity, markPrice, isOrderByQty]);

  //#region Slider value and change handler
  var sliderValue = (0, _react.useMemo)(function () {
    if (hasCredit) {
      return 0;
    }
    var quantity = new _bignumber.BigNumber(formValues.quantity || 0);
    if (maxAmount > 0 && quantity.gt(0)) {
      var newSliderValue = quantity.div(maxAmount).times(100);
      return Math.min(Math.max(newSliderValue.dp(2, _bignumber.BigNumber.ROUND_HALF_UP).toNumber(), 0), 100);
    }
    return 0;
  }, [formValues.quantity, maxAmount, hasCredit]);
  var onSliderChange = (0, _react.useCallback)(function (value) {
    var percentage = parseFloat(value) || 0;
    var amount = maxAmount * (percentage / 100);
    if (anchoredField !== 'quantity') {
      onChange('anchoredField', 'quantity');
    }
    onChange('quantity', new _bignumber.BigNumber(amount).dp(quantityDP).toNumber());
  }, [maxAmount, onChange, anchoredField, quantityDP]);
  //#endregion

  return {
    formValues: formValues,
    sliderValue: sliderValue,
    maxAmount: maxAmount,
    onSliderChange: onSliderChange,
    valueDP: valueDP,
    quantityDP: quantityDP,
    onChange: onChange,
    instrument: instrument,
    isDisabled: isDisabled,
    isConditionalPricefieldDisabled: isConditionalPricefieldDisabled,
    estimatedProfitValue: estimatedProfitValue,
    estimatedLossValue: estimatedLossValue,
    minimumQuantity: minimumQuantity
  };
}